import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BringzzIcon, BringzzText, BringzzImage, BringzzBoxSlider } from "@bringzz/components";
import { ShimmerText, ShimmerImage } from './Shimmer';
import useRequest from 'hooks/useRequest';
import { useNavigation } from 'context/NavigationContext';
import { useDebounce } from "@bringzz/hooks";
import classNames from 'classnames';
import DefaultProductImage from 'assets/DefaultProductImage.png';

const LoadingProduct = () => (
  <div className="flex items-center border-b py-2">
    <ShimmerImage className="h-14 w-14 mr-2 rounded" />
    <div className="flex flex-col flex-grow">
      <ShimmerText className="mb-1" width="w-3/4" />
      <ShimmerText width="w-1/2" />
    </div>
  </div>
);

const ProductsTab = ({ pharmacyId }) => {
  const { t } = useTranslation('pharmacyProfile');
  const { sendRequest } = useRequest();
  const { navigate } = useNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('name_asc');

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const sortOptions = [
    { key: 'name_asc', label: t('tabs.products.sort.name_asc') },
    { key: 'name_desc', label: t('tabs.products.sort.name_desc') },
    { key: 'price_asc', label: t('tabs.products.sort.price_asc') },
    { key: 'price_desc', label: t('tabs.products.sort.price_desc') }
  ];

  const fetchProducts = async (query = '', sort = 'name_asc') => {
    if (!pharmacyId || !query.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/product/searchProducts',
        data: {
          query: query || null,
          pharmacyId: pharmacyId,
          sortBy: sort,
          limit: 50,
          page: 1
        }
      });

      if (response?.status === 'success' && response?.data) {
        setProducts(response.data);
      } else {
        setProducts([]);
        setError(response?.message || t('tabs.products.failed_to_load'));
      }
    } catch (err) {
      setError(err.message || t('tabs.products.failed_to_load'));
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      fetchProducts(debouncedSearchTerm, sortBy);
    } else {
      setProducts([]);
      setLoading(false);
    }
  }, [pharmacyId, debouncedSearchTerm, sortBy]);

  const filteredProducts = useMemo(() => {
    if (!debouncedSearchTerm) return products;
    return products.filter(product => 
      product.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      product.brandName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      product.pzn?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [products, debouncedSearchTerm]);

  if (loading && products.length === 0) {
    return (
      <div className="px-6 pt-2">
        <div className="flex items-center mb-4">
          <ShimmerText className="w-full h-10 rounded-lg" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map((item) => (
            <LoadingProduct key={item} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 pt-4 text-center">
        <BringzzText tag="p" className="text-red-500">{error}</BringzzText>
      </div>
    );
  }

  return (
    <div className="pt-2">
      {/* Search Bar */}
      <div className="px-6 mb-4">
        <div className="relative">
          <BringzzIcon
            icon="IconSearch"
            folder="LineIcons"
            size="16"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder={t('tabs.products.search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-magic-lilac focus:border-transparent"
          />
        </div>
      </div>

      {/* Sort Options */}
      <div className="pl-6 mb-4">
        <BringzzBoxSlider className="pr-4 space-x-2">
          {sortOptions.map((option) => (
            <div 
              key={option.key} 
              className={classNames(
                'bg-sand rounded-lg py-2 px-4 cursor-pointer whitespace-nowrap',
                sortBy === option.key && "!bg-magic-lilac text-white"
              )}
              onClick={() => setSortBy(option.key)}
            >
              <BringzzText tag="h4" className={sortBy === option.key ? "text-white" : ""}>
                {option.label}
              </BringzzText>
            </div>
          ))}
        </BringzzBoxSlider>
      </div>

      {/* Products List */}
      <div className="px-6">
        {searchTerm.trim() && (
          <div className="flex justify-between items-center mb-2">
            <BringzzText tag="h2">{t('tabs.products.title')}</BringzzText>
            <BringzzText tag="h4" className="text-gray-500">
              {filteredProducts.length} {t('tabs.products.items')}
            </BringzzText>
          </div>
        )}
        
        {!searchTerm.trim() ? (
          <div className="text-center py-8">
            <BringzzIcon
              icon="IconSearch"
              folder="LineIcons"
              size="48"
              className="mx-auto mb-4 text-gray-300"
            />
            <BringzzText tag="p" className="text-gray-500">
              {t('tabs.products.search_placeholder')}
            </BringzzText>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-8">
            <BringzzIcon
              icon="IconSearch"
              folder="LineIcons"
              size="48"
              className="mx-auto mb-4 text-gray-300"
            />
            <BringzzText tag="p" className="text-gray-500">
              {t('tabs.products.no_results')}
            </BringzzText>
          </div>
        ) : (
          <div className="space-y-1 pb-24">
            {filteredProducts.map((product) => (
              <div 
                key={product.id} 
                className="flex items-center border-b py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
              >
                <img 
                  src={product.productImage || DefaultProductImage} 
                  className={`h-14 w-14 mr-3 rounded bg-gray-100 ${product.productImage ? 'object-cover' : 'object-contain p-2'}`}
                  alt={product.name}
                  loading="lazy"
                  onError={(e) => {
                    if (e.target.src !== DefaultProductImage) {
                      e.target.src = DefaultProductImage;
                      e.target.className = e.target.className.replace('object-cover', 'object-contain p-2');
                    }
                  }}
                />
                <div className="flex flex-col flex-grow">
                  <BringzzText tag="h3" className="font-semibold">
                    {product.name}
                  </BringzzText>
                  <BringzzText tag="p" className="text-sm text-gray-600">
                    {product.brandName} • PZN: {product.pzn}
                  </BringzzText>
                  <BringzzText tag="p" className="text-sm font-semibold text-magic-lilac">
                    €{parseFloat(product.price || 0).toFixed(2)}
                  </BringzzText>
                </div>
                <BringzzIcon
                  size="16"
                  icon="IconChevronRight"
                  folder="LineIcons"
                  className="text-gray-400"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsTab; 